// The PUBLIC pages' pure kernel (Module 09 §4.10, slice P3-C-1): the wire →
// screen-state normalisation, the locale rule, and the validity countdown.
//
// Everything here is pure and unit-tested. It is separated from the screens
// because all three facts below are decisions somebody could get wrong quietly:
// a 404 that renders the "valid" page, a browser locale that overrides the
// customer's own, and a countdown computed off the DEVICE clock.

import type { components } from "@assessly/api-client";
import { RequestError } from "../../../lib/request";
import { LOCALE_COMPLETENESS } from "../../../i18n-completeness";
import type { Locale } from "../../../i18n-dictionaries";

type S = components["schemas"];

export type PublicQuotePayload = S["PublicQuoteResponse"];
export type PublicOrderPayload = S["PublicOrderResponse"];
export type PublicQuoteLine = S["PublicQuoteLine"];
export type PublicOrderLine = S["PublicOrderLine"];
export type PublicSeller = S["PublicQuoteSeller"];

/**
 * THE FIFTH STATE.
 *
 * `PublicQuoteResponse` is a four-member union of the states a token actually
 * resolves to. §4.10's fifth — not-found — is a 404 carrying the ordinary
 * problem document, because a 200 whose body says "this does not exist" is
 * wrong HTTP (see the contract's own note). The SCREEN still needs one
 * exhaustive switch, so the normaliser below folds that 404 in as a fifth
 * rendering state and the component's switch is total over this type.
 *
 * `error` is the sixth thing that can happen and is NOT a state of the quote:
 * the network failed, or the server did. It renders a retry, never a verdict
 * about the offer — telling a customer their quote does not exist because our
 * ALB hiccupped is the worst sentence on this page.
 */
export type PublicQuoteView =
  | PublicQuotePayload
  | { readonly state: "not_found" }
  | { readonly state: "error"; readonly requestId: string | undefined };

export type PublicOrderView =
  | PublicOrderPayload
  | { readonly state: "not_found" }
  | { readonly state: "error"; readonly requestId: string | undefined };

/** The one shape both doors' 404 wears (the contract's `TOKEN_INVALID`). */
const NOT_FOUND_STATUS = 404;

/**
 * Fold a thrown request failure into a screen state.
 *
 * ONLY 404 becomes `not_found`. A 429 (the §4.10 per-IP rate limit), a 500 and
 * a dead socket all become `error` with the request id — because "not found"
 * is a claim about the customer's link and we may only make it when the server
 * made it. A rate-limited customer who is told their quote does not exist will
 * ring their manager about a quote that is sitting there perfectly valid.
 */
export function quoteViewFromError(error: unknown): PublicQuoteView {
  if (error instanceof RequestError && error.httpStatus === NOT_FOUND_STATUS) {
    return { state: "not_found" };
  }
  return { state: "error", requestId: error instanceof RequestError ? error.requestId : undefined };
}

export function orderViewFromError(error: unknown): PublicOrderView {
  if (error instanceof RequestError && error.httpStatus === NOT_FOUND_STATUS) {
    return { state: "not_found" };
  }
  return { state: "error", requestId: error instanceof RequestError ? error.requestId : undefined };
}

// ---------------------------------------------------------------------------
// the locale rule (§4.10: "by quote/customer locale", never the browser's)
// ---------------------------------------------------------------------------

/**
 * Which locale this page RENDERS in, given the locale recorded on the document.
 *
 * §4.10 says the language comes from the quote/customer record, not from
 * `navigator.languages` — the customer told us once, on the record, and a
 * Russian buyer opening the link on a hotel laptop set to English must still
 * read Russian. So the office app's whole resolution order (`?lng=` > stored >
 * browser > en) is deliberately BYPASSED here.
 *
 * BUT ONLY FOR LOCALES WE ACTUALLY TRANSLATED. A placeholder locale's
 * dictionaries are English text; selecting one would render exactly the same
 * English while stamping `<html lang="zh">`, which lies to screen readers, to
 * hyphenation and to spell-check. A placeholder locale therefore renders as
 * `en` — the same words, honestly labelled — and this function is the single
 * place that decision is made. D-440 promoted fr, zh, zh-Hant and hi in
 * i18n-completeness.ts, which changed this function's answer with no edit
 * here; the server records `PublicPageLocale` (the server-text set) on a
 * document, which holds zh-Hant and hi since L4 (D-590), so a Traditional
 * Chinese or Hindi customer's page now renders in their language.
 */
export function publicRenderLocale(documentLocale: string | undefined): Locale {
  if (documentLocale === undefined) return "en";
  const claim = (LOCALE_COMPLETENESS as Record<string, { status: string } | undefined>)[
    documentLocale
  ];
  if (claim === undefined) return "en";
  return claim.status === "placeholder" ? "en" : (documentLocale as Locale);
}

// ---------------------------------------------------------------------------
// the validity countdown (§4.10 "validity countdown")
// ---------------------------------------------------------------------------

/** §4.5's urgency threshold, stated once on this side as
 * `office/sales/lib/quotes.VALIDITY_URGENT_DAYS` states it on the other. The
 * two files cannot import each other — the public bundle carries no office
 * code — so the number is duplicated on purpose and the comment on each names
 * the other. */
const URGENT_DAYS = 3;

/** Whole days from the SERVER's clock to end of the validity date. `days` is
 * 0 on the LAST day the offer can be accepted — the same convention the office
 * list's `daysRemaining` uses, so the two halves count the same way as well as
 * band the same way. */
export interface Countdown {
  readonly days: number;
  /** FEWER than 3 days left — §4.5's "red when < 3 days", restated on the
   * customer's side so both halves of the same fact agree on what "soon"
   * means. It read `<= 3` until 2026-09-10, which reddened the customer's chip
   * a whole day before the office list reddened the same quote's row: two
   * screens describing one offer with different urgency, and the office is the
   * half a manager acts on. */
  readonly urgent: boolean;
}

/**
 * Days remaining, computed from `server_time` — NEVER `Date.now()`.
 *
 * The device clock is the one input on this page we do not control and cannot
 * see. A laptop three days fast would render "expired" over a live offer and
 * lose the deal; a laptop three days slow would invite an acceptance the
 * server is about to refuse. `server_time` is on the payload precisely so this
 * arithmetic never touches the browser's clock, and the countdown is the only
 * number on the page that would be wrong silently.
 *
 * Both inputs are ISO; the comparison is by CALENDAR DAY in UTC, matching the
 * server's own `valid_until < today_hq` expiry test closely enough that the
 * page and the cron never disagree by a whole day at the boundary. (The exact
 * HQ-timezone midnight is the server's to own — §9.14 — which is why the page
 * renders a DATE beside the count rather than a bare number.)
 */
export function countdownFrom(serverTimeIso: string, validUntil: string): Countdown | undefined {
  const now = Date.parse(serverTimeIso);
  const until = Date.parse(`${validUntil}T23:59:59.999Z`);
  if (!Number.isFinite(now) || !Number.isFinite(until)) return undefined;
  const days = Math.floor((until - now) / 86_400_000);
  const clamped = days < 0 ? 0 : days;
  return { days: clamped, urgent: clamped < URGENT_DAYS };
}

// ---------------------------------------------------------------------------
// same-origin navigation guard
// ---------------------------------------------------------------------------

/**
 * The superseded page auto-forwards to the newest revision's link, and the
 * accepted page links to tracking. Both targets arrive ON THE PAYLOAD, which
 * makes them attacker-shaped data if the server is ever wrong — so a target is
 * only followed when it is a PATH on this origin.
 *
 * The contract already says "a PATH, never a full URL", and this is the
 * client-side half of that: anything with a scheme, a protocol-relative `//`
 * prefix, or a backslash (which some parsers fold to `/`) is refused, and the
 * page renders a manual link instead of navigating. An open redirect on an
 * unauthenticated page that customers reach from a Telegram message is exactly
 * the phishing primitive we must not ship.
 */
export function isSafeInternalPath(path: string | null | undefined): path is string {
  if (typeof path !== "string" || path.length === 0) return false;
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//") || path.startsWith("/\\")) return false;
  return !path.includes("\\");
}
